import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject
} from "@angular/core";
import {
  EventModel,
  PresetManager,
  Scheduler,
  SchedulerConfig
} from "@bryntum/scheduler-thin";
import { Model } from "@bryntum/core-thin";
import moment from "moment";
import { SubDivisionStore } from "@app/services/store/roster/sub-division-store";
import { SubDivision } from "@app/models/rest/roster/sub-division";
import { MatDialog } from "@angular/material/dialog";
import { AddDivisionDialogComponent } from "@app/view/setup/resourcing/roster/add-division-dialog/add-division-dialog.component";
import { Roster } from "@app/models/rest/roster/roster";
import { ConfirmDivisionDeleteDialogComponent } from "@app/view/setup/resourcing/roster/confirm-division-delete-dialog/confirm-division-delete-dialog.component";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ShiftStore } from "@app/services/store/roster/shift-store";
import { ShiftEditorComponent } from "@app/view/setup/resourcing/roster/shift-editor/shift-editor.component";
import { CustomResourceModel } from "@app/models/bryntum-ext/custom-resource-model";
import {
  PagedGetOption,
  ResourceCollection
} from "@lagoshny/ngx-hateoas-client";
import { RosterAssignmentStore } from "@app/services/store/roster/roster-assignment-store";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";

@Component({
  selector: "app-division-grid",
  templateUrl: "./division-grid.component.html",
  styleUrls: ["./division-grid.component.css"],
  imports: [MatButton, MatIcon]
})
export class DivisionGridComponent implements OnInit, OnChanges, AfterViewInit {
  protected element = inject(ElementRef);
  protected subDivisionStore = inject(SubDivisionStore);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  protected shiftStore = inject(ShiftStore);
  protected rosterAssignmentStore = inject(RosterAssignmentStore);
  dialog = inject(MatDialog);

  @Input()
  public roster: Roster;

  @ViewChild("schedulerContainer", { static: false })
  schedulerContainer: ElementRef;

  protected scheduler: Scheduler;
  private init = false;
  private schedulerConfig: Partial<SchedulerConfig>;
  private isSaving = false;

  // Flag to determine if there are any divisions
  public get hasDivisions(): boolean {
    return this.scheduler?.resourceStore?.count > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.init && changes.roster && this.scheduler) {
      // If roster length changed, update the scheduler's time axis
      if (
        changes.roster.currentValue?.length !==
        changes.roster.previousValue?.length
      ) {
        this.updateSchedulerTimeAxis();
      }
      this.loadData();
    }
  }

  openAddShiftDialog() {
    if (!this.roster) return;

    // Prompt to create a division first if there are none
    if (this.scheduler.resourceStore.count === 0) {
      this.openNewDivisionDialog();
      return;
    }

    // Open the shift editor with no existing shift and no pre-selected
    // division - the editor requires the user to pick one so a shift is never
    // silently added to the first division.
    this.openShiftEditor(null, 0, null);
  }

  loadData() {
    console.log("loadData called - roster:", this.roster?.id);

    if (!this.scheduler || !this.roster) {
      console.warn("Cannot load data: scheduler or roster not initialized");
      return;
    }

    console.log("Clearing event and resource stores");
    this.scheduler.eventStore.removeAll();
    // Clear divisions too before reloading them, otherwise repeated loadData()
    // calls (after dialogs / roster changes) leave stale or duplicate rows and
    // re-trigger the assignment-count lookups for records that no longer exist.
    this.scheduler.resourceStore.removeAll();

    console.log("Loading subDivisions from roster");
    this.roster
      .getRelatedCollection<ResourceCollection<SubDivision>>("subDivisions")
      .subscribe(
        (divisions) => {
          console.log(`Loaded ${divisions.resources.length} divisions`);

          divisions.resources.forEach((value) => {
            let division = new CustomResourceModel({
              id: value.id,
              name: value.name
            });

            division.set("entity", value);
            console.log(`Adding division ${value.name} to scheduler`);
            this.scheduler.resourceStore.add(division);
            this.loadActiveAssignmentCount(division);

            this.shiftStore
              .loadWithSearch(
                "findAllBySubDivisionIdEquals",
                {
                  params: {
                    subDivisionId: division.id
                  }
                },
                true
              )
              .then((shifts) => {
                console.log(
                  `Got ${shifts.resources.length} shifts for division ${value.name}`
                );
                shifts.resources.forEach((shift) => {
                  if (this.scheduler.eventStore.getById(shift.id)) {
                    let existing = this.scheduler.eventStore.getById(shift.id);
                    existing.set(
                      "startDate",
                      moment("2020-01-01")
                        .add(shift.offsetDay, "day")
                        .add(shift.startOffsetHour, "hour")
                        .add(shift.startOffsetMinute, "minute")
                        .parseZone()
                        .toDate()
                    );
                    existing.set(
                      "endDate",
                      moment(existing.get("startDate"))
                        .add(shift.duration, "minute")
                        .parseZone()
                        .toDate()
                    );
                    this.scheduler.eventStore.commit();
                  } else {
                    let shiftModel = new EventModel();
                    shiftModel.set("id", shift.id);
                    shiftModel.set(
                      "startDate",
                      moment("2020-01-01")
                        .add(shift.offsetDay, "day")
                        .add(shift.startOffsetHour, "hour")
                        .add(shift.startOffsetMinute, "minute")
                        .parseZone()
                        .toDate()
                    );
                    shiftModel.set(
                      "endDate",
                      moment(shiftModel.startDate)
                        .add(shift.duration, "minute")
                        .parseZone()
                        .toDate()
                    );
                    shiftModel.set("resourceId", division.id);
                    shiftModel.set("entity", shift);

                    this.scheduler.eventStore.add(shiftModel);
                  }
                });
              })
              .catch((error) => {
                console.error(
                  `Error loading shifts for division ${value.name}:`,
                  error
                );
                this.serviceErrorHandler.handleError(error);
              });
          });
        },
        (error) => {
          console.error("Error loading divisions:", error);
          this.serviceErrorHandler.handleError(error);
        }
      );
  }

  ngOnInit() {
    console.log("ngOnInit called - Initializing division grid");

    if (!this.roster) {
      console.warn("No roster provided, using defaults");
    }

    if (this.init) {
      console.log("Already initialized, skipping");
      return;
    } else {
      this.init = true;
    }

    // Prepare scheduler config but don't initialize yet - wait for view init
    this.prepareSchedulerConfig();
  }

  prepareSchedulerConfig() {
    console.log("Preparing scheduler config, roster:", this.roster?.id);

    // Make sure we register the preset first
    console.log("Registering divisionGrid preset");
    PresetManager.registerPreset("divisionGrid", {
      displayDateFormat: "HH:mm",
      shiftIncrement: 1,
      shiftUnit: "day",
      tickWidth: 150,
      timeResolution: {
        unit: "day",
        increment: 1
      },
      headers: [
        {
          unit: "day",
          increment: 1,
          dateFormat: "hh:mm A",
          // The bundled typings say this renderer receives an object, but at
          // runtime Bryntum passes the cell start Date as the first positional
          // arg (an object's `startDate` came back undefined and rendered
          // "Day <today>" on every column). Accept `any` and read whichever
          // shape we get. Round the fractional day diff so the count survives a
          // DST change (a 23h/25h day would otherwise truncate).
          renderer: function (cell: any) {
            const cellStart = cell?.startDate ?? cell;
            return `Day ${
              Math.round(
                moment(cellStart).diff(moment("2020-01-01"), "days", true)
              ) + 1
            }`;
          }
        }
      ]
    });

    // Remove the early return - always configure the scheduler even without roster
    // Just use defaults when the roster isn't available

    let me = this;

    // Default date range
    const defaultStartDate = "2020-01-01";
    const defaultLength = 7; // Default to 7 days if roster length not available

    console.log("Creating scheduler config object");

    // @ts-ignore
    this.schedulerConfig = {
      barMargin: 8,
      rowHeight: 60,
      eventStyle: "filled",
      columnLines: true,
      enableEventAnimations: false,
      allowOverlap: false,
      startDate: defaultStartDate,
      endDate: moment(defaultStartDate)
        .add(this.getRosterLength(), "days")
        .parseZone()
        .toDate(),
      // Don't set appendTo yet, wait for AfterViewInit
      multiEventSelect: false,
      snapRelativeToEventStartDate: false,
      snap: false,
      scrollable: true,
      forceFit: true,
      autoHeight: false,
      height: "calc(100vh - 400px)",
      fillTicks: true,
      zoomOnMouseWheel: false,
      zoomOnTimeAxisDoubleClick: false,
      viewPreset: "divisionGrid",
      cls: "sch-container",
      style: "width: 100%;",
      readOnly: true, // Make scheduler read-only to prevent drag/resize
      eventRenderer: (display: { eventRecord: EventModel; renderData }) => {
        return `${moment(display.eventRecord.startDate).format(
          "HH:mm"
        )} - ${moment(display.eventRecord.endDate).format("HH:mm")}`;
      },
      listeners: {
        beforeEventDelete({ eventRecords, context }) {
          console.log("before event delete");
          me.shiftStore
            .delete(eventRecords[0].get("entity"))
            .subscribe((value) => {
              console.log("done");
              me.scheduler.eventStore.remove(eventRecords);
              // @ts-ignore
              context.finalize(true);
            });
          return false;
        },
        beforeEventSave({ eventRecord, resourceRecords, context, values }) {},
        eventClick: ({ eventRecord, assignmentRecord, event }) => {
          console.log("Event clicked", eventRecord);
          const shift = eventRecord.get("entity");
          if (shift) {
            // Handle existing shift
            me.openShiftEditor(shift.id, null, null);
          } else {
            // Handle ghost shift
            me.openShiftEditor(
              null,
              me.dayOffsetFromDate(eventRecord.startDate),
              assignmentRecord.getResource().get("entity")
            );
          }
          return false;
        },
        // Prevent double-click handling
        cellDblClick: () => {
          console.log("Cell double-click prevented");
          return false; // Prevent default behavior
        },
        scheduleClick: () => {
          return false; // Prevent default click behavior
        },
        // Double-clicking an empty cell opens the shift editor for that day
        // and division.
        scheduleDblClick: ({ date, resourceRecord }) => {
          if (resourceRecord) {
            me.openShiftEditor(
              null,
              me.dayOffsetFromDate(date),
              resourceRecord.get("entity")
            );
          }
          return false;
        }
      },
      columns: [
        {
          id: "divisionName",
          type: "template",
          locked: true,
          draggable: false,
          text: "Division",
          field: "name",
          editor: false,
          fitMode: "textContent",
          width: 200,
          autoHeight: true,
          template: ({ record }) => {
            const name = record.get("name") || "";
            const count = record.get("activeAssignments");
            const nameLine = `<div class="division-name">${name}</div>`;
            if (count == null) {
              return nameLine;
            }
            const label =
              count === 1 ? "active assignment" : "active assignments";
            return `${nameLine}<div class="division-assignment-count">${count} ${label}</div>`;
          }
        },
        {
          type: "action",
          locked: true,
          draggable: false,
          text: "",
          width: 60,
          align: "center",
          actions: [
            {
              cls: "b-fa b-fa-trash",
              tooltip: "Delete Division",
              onClick: ({ record }) => {
                me.confirmDeleteDivision(record);
              }
            }
          ]
        }
      ],
      features: {
        eventDrag: false, // Disable dragging events
        eventDragCreate: false, // Disable drag create - use add button instead
        eventEdit: false,
        eventResize: false, // Disable event resizing
        eventMenu: {
          items: {
            editEvent: {
              text: "Edit Shift",
              icon: "b-fa b-fa-edit",
              weight: 100,
              onItem: ({ eventRecord }) => {
                const shift = eventRecord.get("entity");
                if (shift) {
                  me.openShiftEditor(shift.id, null, null);
                }
              }
            },
            deleteEvent: {
              text: "Delete Shift",
              icon: "b-fa b-fa-trash",
              weight: 200,
              onItem: ({ eventRecord }) => {
                const shift = eventRecord.get("entity");
                if (shift) {
                  me.shiftStore.delete(shift).subscribe(
                    () => {
                      me.scheduler.eventStore.remove(eventRecord);
                      me.loadData();
                    },
                    (error) => me.serviceErrorHandler.handleError(error)
                  );
                }
              }
            },
            unassignEvent: false
          }
        },
        rowCopyPaste: false,
        eventCopyPaste: false,
        columnResize: false,
        regionResize: false,
        eventFilter: false,
        eventTooltip: {
          template: (data) => {
            const { eventRecord } = data;
            const startTime = moment(eventRecord.startDate).format("HH:mm");
            const endTime = moment(eventRecord.endDate).format("HH:mm");
            const durationInHours = (
              eventRecord.durationMS /
              (1000 * 60 * 60)
            ).toFixed(1);

            return `
              <div class="event-tooltip">
                <div class="tooltip-title">Shift</div>
                <div>Start: ${startTime}</div>
                <div>End: ${endTime}</div>
                <div>Duration: ${durationInHours} hours</div>
                <div class="tooltip-footer">Click to edit details</div>
              </div>
            `;
          }
        },
        scheduleTooltip: false,
        timeRanges: { showHeaderElements: true, showCurrentTimeLine: false },
        resourceTimeRanges: true,
        cellEdit: false,
        filter: false,
        timeAxisHeaderMenu: {}
      }
    };
  }

  ngAfterViewInit() {
    console.log("ngAfterViewInit - initializing scheduler");

    // Now we can safely use the schedulerContainer reference
    setTimeout(() => {
      if (!this.schedulerContainer) {
        console.error("Scheduler container reference not available");
        return;
      }

      // Make sure the scheduler config is prepared
      if (!this.schedulerConfig) {
        console.log("Preparing scheduler config");
        this.prepareSchedulerConfig();
      }

      if (!this.schedulerConfig) {
        console.error("Failed to prepare scheduler config");
        return;
      }

      console.log("Initializing scheduler", this.schedulerContainer);

      // Create a fresh config object to avoid reference issues
      const config = {
        ...this.schedulerConfig,
        appendTo: this.schedulerContainer.nativeElement
      };

      // Initialize the scheduler with the config
      this.scheduler = new Scheduler(config);

      console.log("Initializing complete, calling loadData()");
      // Now that the scheduler is initialized, load the data
      this.loadData();
    }, 0);
  }

  openShiftEditor(
    existingId: string | number,
    offsetDay: number,
    subDivision: SubDivision
  ): void {
    let dialogRef = this.dialog.open(ShiftEditorComponent, {
      data: {
        shiftId: existingId,
        offsetDay: offsetDay,
        subDivision: subDivision,
        roster: this.roster
      }
    });
    dialogRef.afterClosed().subscribe((value) => {
      console.log("closed", value);
      this.loadData();
    });
  }

  openNewDivisionDialog(): void {
    const dialogRef = this.dialog.open(AddDivisionDialogComponent, {
      data: { roster: this.roster }
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("The dialog was closed");
      this.loadData();
      // console.log(result);
    });
  }

  confirmDeleteDivision(record: Model): void {
    const dialogRef = this.dialog.open(ConfirmDivisionDeleteDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.subDivisionStore.delete(record.get("entity")).subscribe(
          () => {
            this.scheduler.resourceStore.remove(record);
            this.loadData();
          },
          (error) => this.serviceErrorHandler.handleError(error)
        );
      }
    });
  }

  private dayOffsetFromDate(date: Date | string): number {
    // Round the fractional day diff so the day stays correct across a DST
    // change (a 23h/25h day would otherwise truncate to the wrong day),
    // matching the day-header renderer.
    return Math.round(moment(date).diff(moment("2020-01-01"), "days", true));
  }

  private loadActiveAssignmentCount(division: CustomResourceModel): void {
    const subDivision = division.get("entity");
    if (!subDivision?.id) {
      return;
    }
    // "Active" = assignments without an end date in the past. Count all
    // assignments for the division and subtract those that ended before today.
    // A null end date never matches "ended before today" so it stays active,
    // and "LessThan today" maps to before(startOfDay) server-side, so an
    // assignment ending today is kept active too. size:1 means we only read the
    // page's totalElements, not the records.
    const params: PagedGetOption = { pageParams: { size: 1 } };
    const startOfToday = moment().startOf("day").toISOString();
    const inDivision = {
      key: "subDivision.id",
      operation: "Equals",
      value: subDivision.id
    };
    const total = this.rosterAssignmentStore
      .advancedSearch(params, [inDivision])
      .then((page) => page.totalElements ?? 0);
    const endedBeforeToday = this.rosterAssignmentStore
      .advancedSearch(params, [
        inDivision,
        { key: "endDate", operation: "LessThan", value: startOfToday }
      ])
      .then((page) => page.totalElements ?? 0);

    Promise.all([total, endedBeforeToday])
      .then(([all, past]) => {
        division.set("activeAssignments", all - past);
      })
      .catch((error) => {
        console.error("Error loading active assignment count", error);
      });
  }

  private getRosterLength(): number {
    // If roster and length are available, use them
    if (this.roster?.length && this.roster.length > 0) {
      return this.roster.length;
    }
    return 7;
  }

  private updateSchedulerTimeAxis(): void {
    if (!this.scheduler) return;

    const startDate = moment("2020-01-01").parseZone().toDate();
    const newEndDate = moment("2020-01-01")
      .add(this.getRosterLength(), "days")
      .parseZone()
      .toDate();

    this.scheduler.setTimeSpan(startDate, newEndDate);
  }
}
